/**
 * BRUME - Main JavaScript Entry Point (Shopify)
 * Immersive water-themed landing page with advanced interactive features
 */

(function() {
  'use strict';

  // Initialize everything when DOM is ready
  document.addEventListener('DOMContentLoaded', function() {
    // Initialize header behavior
    if (typeof Brume !== 'undefined' && Brume.initHeader) {
      Brume.initHeader();
    }

    // Initialize cart system
    if (typeof Brume !== 'undefined' && Brume.initCartDrawer) {
      Brume.initCartDrawer();
    }
    if (typeof Brume !== 'undefined' && Brume.initCartIcon) {
      Brume.initCartIcon();
    }

    // Initialize scroll-triggered animations
    if (typeof Brume !== 'undefined' && Brume.initScrollAnimations) {
      Brume.initScrollAnimations();
    }

    // Initialize scroll depth color shift
    if (typeof Brume !== 'undefined' && Brume.initScrollDepth) {
      Brume.initScrollDepth();
    }

    // Initialize How It Works scrollytelling
    if (typeof Brume !== 'undefined' && Brume.initHowItWorks) {
      Brume.initHowItWorks();
    }

    // Initialize custom water cursor
    if (typeof Brume !== 'undefined' && Brume.initWaterCursor) {
      Brume.initWaterCursor();
    }

    // Text reveal disabled — hero logo visibility controlled by video cycle
    // Brume.initTextReveal();

    // Initialize droplet journey scrollytelling
    if (typeof Brume !== 'undefined' && Brume.initDropletJourney) {
      Brume.initDropletJourney();
    }

    // Initialize horizontal scroll for feature cards
    if (typeof Brume !== 'undefined' && Brume.initHorizontalScroll) {
      Brume.initHorizontalScroll();
    }

    // Initialize product slideshow
    if (typeof Brume !== 'undefined' && Brume.initSlideshow) {
      Brume.initSlideshow();
    }

    // Hero video: simple continuous loop, muted, no overlays or cycles.
    var heroVideo = document.querySelector('.hero-bg-video');
    var heroHeadline = document.querySelector('.hero-headline-large');
    if (heroVideo) {
      var heroBg = heroVideo.closest('.hero-bg');

      // Fade the video in once it can play, so a buffering stall never shows
      // as a blank/janky frame (the poster background stays visible until then).
      heroVideo.classList.add('is-loading');
      function revealHero() { heroVideo.classList.remove('is-loading'); }
      if (heroVideo.readyState >= 3) {
        revealHero();
      } else {
        heroVideo.addEventListener('canplay', revealHero, { once: true });
        heroVideo.addEventListener('playing', revealHero, { once: true });
      }

      // Mobile gets the vertical video; desktop uses the markup <source> tags (WebM then MP4).
      if (window.innerWidth <= 768) {
        if (window.BrumeConfig && window.BrumeConfig.mobileVideoUrl) {
          // Drop the desktop sources (webm/mp4) and load the mobile file directly.
          while (heroVideo.firstChild) heroVideo.removeChild(heroVideo.firstChild);
          heroVideo.src = window.BrumeConfig.mobileVideoUrl;
          if (window.BrumeConfig.mobilePosterUrl) {
            heroVideo.poster = window.BrumeConfig.mobilePosterUrl;
            if (heroBg) heroBg.style.backgroundImage = "url('" + window.BrumeConfig.mobilePosterUrl + "')";
          }
          heroVideo.load();
        }
      } else {
        // Desktop: eagerly buffer the video so playback is smooth, not laggy.
        // The browser picks the smaller WebM (VP9) if it can, else falls back to MP4.
        heroVideo.preload = 'auto';
        heroVideo.load();
      }

      // Desktop: play the video once, then crossfade to the still image.
      // Mobile keeps looping continuously.
      var heroIsDesktop = window.innerWidth > 768;
      var heroDone = false;
      if (heroIsDesktop) {
        heroVideo.loop = false;
        heroVideo.addEventListener('ended', function () {
          heroDone = true; // single play — crossfade to the still image
          var heroEl = document.getElementById('hero');
          if (heroEl) heroEl.classList.add('hero-ended');
        });
      } else {
        heroVideo.loop = true;
      }
      if (heroHeadline) heroHeadline.classList.add('hero-logo-hidden');

      function playHero() {
        var p = heroVideo.play();
        if (p && p.catch) p.catch(function () {});
      }
      playHero();

      // Resume if a backgrounded tab restored the video paused at frame 0
      // (but never restart once the desktop single play has finished).
      function resumeHeroVideo() {
        if (document.visibilityState === 'visible' && heroVideo.paused && !heroDone) {
          playHero();
        }
      }
      document.addEventListener('visibilitychange', resumeHeroVideo);
      window.addEventListener('pageshow', resumeHeroVideo);
    }

    // Gently fade the hero text out as the user scrolls down (mobile + desktop).
    // Drives the --splash-fade multiplier so the per-breakpoint base opacity is preserved.
    // Targets: mobile splash line + the desktop headline stack.
    var fadeTargets = [
      document.querySelector('.hero-splash-text'),
      document.querySelector('.hero-stack')
    ].filter(Boolean);
    var heroSection = document.getElementById('hero');
    if (fadeTargets.length && heroSection) {
      var fadeTicking = false;
      function updateSplashFade() {
        fadeTicking = false;
        // How far the hero has scrolled up past the top of the viewport.
        var scrolled = -heroSection.getBoundingClientRect().top;
        if (scrolled < 0) scrolled = 0;
        var fadeDistance = window.innerHeight * 0.6; // fully gone after ~60% of a screen
        var fade = 1 - (scrolled / fadeDistance);
        if (fade < 0) fade = 0;
        if (fade > 1) fade = 1;
        fadeTargets.forEach(function (el) { el.style.setProperty('--splash-fade', fade); });
      }
      function onSplashScroll() {
        if (!fadeTicking) { fadeTicking = true; requestAnimationFrame(updateSplashFade); }
      }
      window.addEventListener('scroll', onSplashScroll, { passive: true });
      window.addEventListener('resize', onSplashScroll, { passive: true });
      updateSplashFade();
    }

    // Features carousel: horizontal scroll with prev/next arrows
    var fTrack = document.getElementById('featuresTrack');
    if (fTrack) {
      var fPrev = document.querySelector('.features-nav-prev');
      var fNext = document.querySelector('.features-nav-next');

      function fStep() {
        var card = fTrack.querySelector('.features-card');
        if (!card) return fTrack.clientWidth;
        var styles = getComputedStyle(fTrack);
        var gap = parseFloat(styles.columnGap || styles.gap) || 0;
        return card.getBoundingClientRect().width + gap;
      }

      function fUpdateArrows() {
        var max = fTrack.scrollWidth - fTrack.clientWidth;
        // Desktop shows all cards (nothing to scroll): keep arrows enabled-looking
        if (max <= 2) {
          if (fPrev) fPrev.disabled = false;
          if (fNext) fNext.disabled = false;
          return;
        }
        if (fPrev) fPrev.disabled = fTrack.scrollLeft <= 0;
        if (fNext) fNext.disabled = fTrack.scrollLeft >= max - 1;
      }

      if (fPrev) fPrev.addEventListener('click', function() {
        fTrack.scrollBy({ left: -fStep(), behavior: 'smooth' });
      });
      if (fNext) fNext.addEventListener('click', function() {
        fTrack.scrollBy({ left: fStep(), behavior: 'smooth' });
      });

      fTrack.addEventListener('scroll', fUpdateArrows, { passive: true });
      window.addEventListener('resize', fUpdateArrows);
      fUpdateArrows();
    }

    // Mobile finish carousel: dots synced with horizontal scroll
    var finishTrack = document.getElementById('finishCarousel');
    var finishDots = document.getElementById('finishDots');
    if (finishTrack && finishDots) {
      var finishCards = finishTrack.querySelectorAll('.finish-card');

      finishCards.forEach(function(_, i) {
        var dot = document.createElement('button');
        dot.className = 'finish-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', 'Go to item ' + (i + 1));
        dot.addEventListener('click', function() {
          var card = finishCards[i];
          if (card) finishTrack.scrollTo({ left: card.offsetLeft - finishTrack.offsetLeft, behavior: 'smooth' });
        });
        finishDots.appendChild(dot);
      });

      function finishUpdateDots() {
        var index = Math.round(finishTrack.scrollLeft / (finishTrack.scrollWidth / finishCards.length));
        if (index < 0) index = 0;
        if (index > finishCards.length - 1) index = finishCards.length - 1;
        finishDots.querySelectorAll('.finish-dot').forEach(function(d, i) {
          d.classList.toggle('active', i === index);
        });
      }

      finishTrack.addEventListener('scroll', finishUpdateDots, { passive: true });
    }

    // Features carousel dots (mobile): synced with horizontal scroll
    var featTrack = document.getElementById('featuresTrack');
    var featDots = document.getElementById('featuresDots');
    if (featTrack && featDots) {
      var featCards = featTrack.querySelectorAll('.features-card');

      featCards.forEach(function(_, i) {
        var dot = document.createElement('button');
        dot.className = 'features-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.addEventListener('click', function() {
          var card = featCards[i];
          if (card) featTrack.scrollTo({ left: card.offsetLeft - featTrack.offsetLeft, behavior: 'smooth' });
        });
        featDots.appendChild(dot);
      });

      function featUpdateDots() {
        var index = Math.round(featTrack.scrollLeft / (featTrack.scrollWidth / featCards.length));
        if (index < 0) index = 0;
        if (index > featCards.length - 1) index = featCards.length - 1;
        featDots.querySelectorAll('.features-dot').forEach(function(d, i) {
          d.classList.toggle('active', i === index);
        });
      }

      featTrack.addEventListener('scroll', featUpdateDots, { passive: true });
    }

    // Blog showcase carousel dots (mobile): synced with horizontal scroll
    var blogTrack = document.getElementById('blogCarousel');
    var blogDots = document.getElementById('blogDots');
    if (blogTrack && blogDots) {
      var blogSlides = blogTrack.querySelectorAll('.blog-slide');

      blogSlides.forEach(function(_, i) {
        var dot = document.createElement('button');
        dot.className = 'blog-dot' + (i === 0 ? ' active' : '');
        dot.setAttribute('aria-label', 'Go to article ' + (i + 1));
        dot.addEventListener('click', function() {
          var slide = blogSlides[i];
          if (slide) blogTrack.scrollTo({ left: slide.offsetLeft - blogTrack.offsetLeft, behavior: 'smooth' });
        });
        blogDots.appendChild(dot);
      });

      function blogUpdateDots() {
        var index = Math.round(blogTrack.scrollLeft / (blogTrack.scrollWidth / blogSlides.length));
        if (index < 0) index = 0;
        if (index > blogSlides.length - 1) index = blogSlides.length - 1;
        blogDots.querySelectorAll('.blog-dot').forEach(function(d, i) {
          d.classList.toggle('active', i === index);
        });
      }

      blogTrack.addEventListener('scroll', blogUpdateDots, { passive: true });
    }

    // Log for debugging
  });

  // Smooth scroll for anchor links
  document.querySelectorAll('a[href^="#"]').forEach(function(anchor) {
    anchor.addEventListener('click', function(e) {
      e.preventDefault();
      var target = document.querySelector(this.getAttribute('href'));
      if (target) {
        target.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    });
  });

})();
